/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Mic, Square, RotateCcw, AlertTriangle, MicOff, Keyboard } from 'lucide-react';
import { cn } from '@/lib/utils';

// Barre decorative dell'onda audio: lista statica con chiavi stabili (no index-as-key)
const WAVE_BARS = ['w1', 'w2', 'w3', 'w4', 'w5', 'w6', 'w7', 'w8'];

/**
 * VoiceRecorder — registratore vocale TOUCH-FIRST per OSS/infermieri in mobilità.
 *
 * Accessibilità & UX:
 *  - Pulsante principale 88px (ben oltre i 48px WCAG 2.5.5), area tap ampia.
 *  - Feedback visivo continuo: anello pulsante + barre "waveform" durante la
 *    registrazione (solo transform/opacity → 60fps anche su device economici).
 *  - Trascrizione live con testo provvisorio (interim) evidenziato.
 *  - Fallback tastiera se il browser non supporta Web Speech (Firefox).
 *
 * Props:
 *   value, onChange, maxChars, disabled, compact (layout ridotto)
 */
export const VoiceRecorder = ({
  value = '',
  onChange,
  maxChars = 12000,
  disabled = false,
  compact = false,
}) => {
  const [recording, setRecording] = useState(false);
  const [supported, setSupported] = useState(true);
  const [interim, setInterim] = useState('');
  const [error, setError] = useState('');
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const recognitionRef = useRef(null);
  const baseValueRef = useRef(value);
  const timerRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSupported(false); setShowKeyboard(true); return undefined; }
    const rec = new SR();
    rec.lang = 'it-IT';
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    rec.onresult = (event) => {
      let interimText = '';
      let finalText = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const chunk = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalText += chunk;
        else interimText += chunk;
      }
      if (finalText) baseValueRef.current = `${baseValueRef.current}${baseValueRef.current && !baseValueRef.current.endsWith(' ') ? ' ' : ''}${finalText.trim()}`;
      setInterim(interimText.trim());
      onChange?.(baseValueRef.current.slice(0, maxChars));
    };
    rec.onerror = (e) => {
      setError(e.error === 'not-allowed'
        ? 'Permesso microfono negato: abilitalo nelle impostazioni del browser'
        : e.error === 'no-speech'
          ? 'Nessuna voce rilevata, riprova'
          : `Errore: ${e.error}`);
      setRecording(false);
    };
    rec.onend = () => { setRecording(false); setInterim(''); };
    recognitionRef.current = rec;
    return () => { try { rec.stop(); } catch (e) { /* ignore */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxChars]);

  useEffect(() => { baseValueRef.current = value; }, [value]);

  // Timer di registrazione
  useEffect(() => {
    if (recording) {
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current); timerRef.current = null;
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [recording]);

  const start = useCallback(() => {
    setError('');
    baseValueRef.current = value;
    try {
      recognitionRef.current?.start();
      setRecording(true);
      if (navigator.vibrate) navigator.vibrate(30);
    } catch (e) {
      setError('Impossibile avviare la registrazione');
    }
  }, [value]);

  const stop = useCallback(() => {
    try { recognitionRef.current?.stop(); } catch (e) { /* ignore */ }
    setRecording(false);
    setInterim('');
    if (navigator.vibrate) navigator.vibrate([20, 40, 20]);
  }, []);

  const reset = useCallback(() => {
    stop();
    baseValueRef.current = '';
    onChange?.('');
  }, [stop, onChange]);

  const chars = value.length;
  const pct = Math.min(100, Math.round((chars / maxChars) * 100));
  const mmss = `${String(Math.floor(elapsed / 60)).padStart(2, '0')}:${String(elapsed % 60).padStart(2, '0')}`;

  return (
    <div className="space-y-3" data-testid="voice-recorder">
      {!supported && (
        <div className="flex items-start gap-2 text-sm rounded-xl border border-warning/40 bg-warning/10 text-foreground p-3">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5 text-warning" />
          <div>Il browser non supporta il riconoscimento vocale. Usa Chrome/Edge/Safari oppure digita la nota qui sotto.</div>
        </div>
      )}

      {/* Big touch control */}
      <div className={cn('flex items-center gap-4', compact ? 'flex-row' : 'flex-col sm:flex-row')}>
        {supported && (
          <button
            type="button"
            onClick={recording ? stop : start}
            disabled={disabled}
            aria-pressed={recording}
            aria-label={recording ? 'Ferma registrazione' : 'Avvia registrazione'}
            className={cn(
              'relative flex-shrink-0 rounded-full flex items-center justify-center transition-transform duration-150 active:scale-95',
              'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/40 disabled:opacity-50',
              compact ? 'h-16 w-16' : 'h-[88px] w-[88px]',
              recording ? 'bg-destructive text-destructive-foreground rec-ring' : 'bg-primary text-primary-foreground shadow-elev-md',
            )}
            data-testid={recording ? 'voice-stop-btn' : 'voice-start-btn'}
          >
            {recording ? <Square className={compact ? 'h-6 w-6' : 'h-8 w-8'} /> : <Mic className={compact ? 'h-7 w-7' : 'h-10 w-10'} />}
          </button>
        )}

        <div className="flex-1 min-w-0 w-full space-y-2">
          <div className="flex items-center gap-3">
            {/* Waveform */}
            <div className="flex items-center gap-[3px] h-8" aria-hidden="true">
              {WAVE_BARS.map((k) => (
                <span key={k} className={cn('block w-1 h-full rounded-full', recording ? 'bg-destructive wave-bar' : 'bg-muted-foreground/30 scale-y-[0.3]')} />
              ))}
            </div>
            <div className="text-sm">
              {recording ? (
                <span className="font-semibold text-destructive tabular-nums" data-testid="voice-recording-timer">● REC {mmss}</span>
              ) : (
                <span className="text-muted-foreground">{supported ? 'Tocca il microfono e parla in italiano' : 'Digita la nota'}</span>
              )}
            </div>
          </div>
          {interim && (
            <div className="text-sm italic text-muted-foreground truncate animate-fade-slide" data-testid="voice-interim">“{interim}”</div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={reset} disabled={disabled || (!value && !recording)} className="touch-target" data-testid="voice-reset-btn">
              <RotateCcw className="h-4 w-4" /> Reset
            </Button>
            {supported && (
              <Button size="sm" variant="ghost" onClick={() => setShowKeyboard((v) => !v)} className="touch-target" data-testid="voice-toggle-keyboard" aria-pressed={showKeyboard}>
                <Keyboard className="h-4 w-4" /> {showKeyboard ? 'Nascondi testo' : 'Modifica testo'}
              </Button>
            )}
            <span className="ml-auto text-[11px] text-muted-foreground tabular-nums">{chars.toLocaleString('it-IT')} / {maxChars.toLocaleString('it-IT')}</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="text-sm text-destructive flex items-center gap-1.5" role="alert" data-testid="voice-error">
          <MicOff className="h-4 w-4" /> {error}
        </div>
      )}

      {/* Live transcript (read-only card) or editable textarea */}
      {showKeyboard ? (
        <Textarea
          value={value}
          onChange={(e) => onChange?.(e.target.value.slice(0, maxChars))}
          placeholder="Scrivi o correggi la trascrizione…"
          rows={5}
          className={cn('text-base rounded-xl', recording && 'ring-2 ring-destructive/40')}
          disabled={disabled}
          data-testid="voice-transcript-textarea"
        />
      ) : (
        <div
          className={cn('rounded-xl border bg-card p-3 min-h-[96px] text-base leading-relaxed whitespace-pre-wrap', recording ? 'border-destructive/40' : 'border-border/70', !value && 'text-muted-foreground')}
          data-testid="voice-transcript-live"
          aria-live="polite"
        >
          {value || 'La trascrizione apparirà qui in tempo reale…'}
          {recording && <span className="stream-caret" />}
        </div>
      )}
      <div className="h-1 w-full rounded-full bg-muted overflow-hidden">
        <div className="h-full bg-primary transition-[width] duration-200" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export default VoiceRecorder;
